import {
  BufferGeometry,
  Camera,
  Group,
  Intersection,
  Material,
  Matrix4,
  Raycaster,
  Scene,
  WebGLRenderer,
} from 'three';
import { BatchedText, Text } from 'troika-three-text';

/**
 * Called before each render of the batch, to update a text for the camera, e.g. to face it.
 */
export type TextUpdate = (renderer: WebGLRenderer, camera: Camera) => void;

/**
 * Draws many texts in one draw call, e.g. the texts of a layer of a drawing or the height labels of
 * an asset, which can be thousands. Each text drawn on its own costs as much as a large mesh to
 * render.
 *
 * The texts are added with add or addText, and are members of the batch, not children: they are
 * placed in the coordinates of the batch and hidden with it. A text is left out while it is not
 * visible itself. The texts are intersected one by one, as the object of the batch, and freed with
 * its geometry, also when it is disposed by disposeGeometriesAndMaterials.
 */
export class TextBatch extends BatchedText {
  /** The texts of the batch, in the order they were added, also the ones that are not visible. */
  readonly texts: Text[] = [];

  /** The texts that are drawn, the visible ones. */
  private readonly drawn = new Set<Text>();
  private readonly updates = new Map<Text, TextUpdate>();
  private disposed = false;

  constructor() {
    super();
    this.geometry.addEventListener('dispose', () => this.dispose());
  }

  /**
   * Adds the text, with a function that updates it before each render.
   */
  override addText(text: Text, update?: TextUpdate) {
    if (!this.texts.includes(text)) {
      this.texts.push(text);
    }

    if (update) {
      this.updates.set(text, update);
    }

    this.updateDrawn(text);
  }

  override removeText(text: Text) {
    const index = this.texts.indexOf(text);

    if (index >= 0) {
      this.texts.splice(index, 1);
    }

    this.updates.delete(text);

    if (this.drawn.delete(text)) {
      super.removeText(text);
    }
  }

  /**
   * Gets the matrix of the text in world coordinates, which it does not have itself, since it is
   * not a child of the batch.
   */
  getTextMatrixWorld(text: Text, target = new Matrix4()): Matrix4 {
    text.updateMatrix();
    return target.multiplyMatrices(this.matrixWorld, text.matrix);
  }

  override updateMatrixWorld(force?: boolean) {
    for (const text of this.texts) {
      this.updateDrawn(text);
    }

    super.updateMatrixWorld(force);
  }

  override onBeforeRender(
    renderer: WebGLRenderer,
    scene: Scene,
    camera: Camera,
    geometry: BufferGeometry,
    material: Material,
    group: Group,
  ) {
    // Which texts are visible is up to date: the scene's updateMatrixWorld updated it.
    this.runUpdates(renderer, camera);
    super.onBeforeRender(renderer, scene, camera, geometry, material, group);
  }

  /**
   * Updates the visible texts for the camera, as before each render: e.g. the size and text of
   * labels, which are not set until then. Also between renders, e.g. for an export, for the texts
   * that have been shown since the last render too.
   */
  updateTexts(renderer: WebGLRenderer, camera: Camera) {
    for (const text of this.texts) {
      this.updateDrawn(text);
    }

    this.runUpdates(renderer, camera);
  }

  private runUpdates(renderer: WebGLRenderer, camera: Camera) {
    for (const [text, update] of this.updates) {
      if (this.drawn.has(text)) {
        update(renderer, camera);
        text.updateMatrix();
      }
    }
  }

  /**
   * Intersects the visible texts, since the batch has no geometry of its own to intersect.
   */
  override raycast(raycaster: Raycaster, intersects: Intersection[]) {
    const found: Intersection[] = [];

    for (const text of this.drawn) {
      this.getTextMatrixWorld(text, text.matrixWorld);
      text.raycast(raycaster, found);
    }

    for (const intersection of found) {
      intersection.object = this;
      intersects.push(intersection);
    }
  }

  /**
   * Frees the batch and its texts. The geometry of the batch is disposed too.
   */
  override dispose() {
    if (this.disposed) {
      return;
    }

    this.disposed = true;
    super.dispose();

    for (const text of this.texts) {
      text.dispose();
    }
  }

  /**
   * Draws the text if it is visible, or leaves it out if it is not.
   */
  private updateDrawn(text: Text) {
    if (text.visible === this.drawn.has(text)) {
      return;
    }

    if (text.visible) {
      this.drawn.add(text);
      super.addText(text);
    } else {
      this.drawn.delete(text);
      super.removeText(text);
    }
  }
}
